<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useForm } from 'vee-validate';
import analytics, {
  SEGMENT_EVENTS,
  trackBusinessProfileStepCompletion,
} from '@/utils/analytics';
import { RouteNames } from '@/data/routes';
import { getParsedMessage } from '@/data/error-messages';
import { useProfiling } from '@/composables/useProfiling';

import DSButton from 'primevue/button';
import BusinessAddressFields from './BusinessAddressFields.vue';
import BusinessInfoFields from './BusinessInfoFields.vue';
import BusinessIncorporationFields from './BusinessIncorporationFields.vue';

interface BusinessDetailsFields {
  validateAndGetValues: () => Promise<any>;
}

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();
const emits = defineEmits(['validationSuccess', 'validationFailure']);
const profiling = useProfiling(RouteNames.BUSINESS.INDEX, {
  type: store.getters.eventTypeBasedOnLoginsCount,
});

const businessInfoFields = ref<BusinessDetailsFields>();
const businessAddressFields = ref<BusinessDetailsFields>();
const businessIncorporationFields = ref<BusinessDetailsFields>();

// ---------
// Variables
// ---------

const requestStatuses = reactive({
  updateBusiness: computed(() =>
    store.getters.requestStatus('UPDATE_BUSINESS'),
  ),
});

// -------
// Methods
// -------

function handleGoBack() {
  router.push({
    name: RouteNames.PROFILE.INDEX,
  });
}

const { handleSubmit, isSubmitting } = useForm();

const onSubmit = handleSubmit(async () => {
  const infoValues = await businessInfoFields.value?.validateAndGetValues();
  const addressValues =
    await businessAddressFields.value?.validateAndGetValues();
  const incorporationValues =
    await businessIncorporationFields.value?.validateAndGetValues();

  if (!infoValues || !addressValues || !incorporationValues) {
    return;
  }
  await store.dispatchApiAction('UPDATE_BUSINESS', {
    ...addressValues,
    ...infoValues,
    ...incorporationValues,
  });

  if (requestStatuses.updateBusiness.isError) {
    emits(
      'validationFailure',
      getParsedMessage(requestStatuses.updateBusiness.error, true),
    );
    return;
  }

  profiling.finish();
  analytics.track(SEGMENT_EVENTS.if_business_details_completed);
  trackBusinessProfileStepCompletion(store.getters.businessProfileSteps);
  emits('validationSuccess');
});
</script>

<template>
  <form
    class="c-form"
    :class="$style.form"
    :style="{ pointerEvents: isSubmitting ? 'none' : 'all' }"
    @submit="onSubmit"
  >
    <BusinessInfoFields
      :business="store.getters.business"
      ref="businessInfoFields"
    />

    <BusinessAddressFields
      :business="store.getters.business"
      ref="businessAddressFields"
    />

    <BusinessIncorporationFields
      :business="store.getters.business"
      ref="businessIncorporationFields"
    />

    <div class="c-form__controls">
      <DSButton label="Back" class="p-button-outlined" @click="handleGoBack" />
      <DSButton label="Next" type="submit" :loading="isSubmitting" />
    </div>
  </form>
</template>

<style module scoped lang="less">
.form {
  margin: 40px 0 0;
}

div[class^='c-form__row']:empty {
  margin: 0;
}
</style>
